import React from "react";
import { useAuthStore } from "@/stores/auth.store";
import { PERMISSIONS } from "@/constants/permissions";

/**
 * Hook to check user permissions throughout the application
 * Returns permission checking utilities
 */
export function usePermissions() {
  const user = useAuthStore((state) => state.user);
  const hasPermission = useAuthStore((state) => state.hasPermission);
  const hasAnyPermission = useAuthStore((state) => state.hasAnyPermission);
  const hasAllPermissions = useAuthStore((state) => state.hasAllPermissions);

  // Check if user is a super user (super_admin or tenant_owner)
  const isSuperUser =
    user?.role === "super_admin" || user?.role === "tenant_owner";
  const isAdmin = user?.role === "admin" || isSuperUser;
  const isManager = user?.role === "manager" || isAdmin;

  // Permission check helpers
  const can = (permission: string) => hasPermission(permission);
  const canAny = (permissions: string[]) => hasAnyPermission(permissions);
  const canAll = (permissions: string[]) => hasAllPermissions(permissions);

  // Module-specific permission checks
  const permissions = {
    // Dashboard
    canViewDashboard: () => can(PERMISSIONS.DASHBOARD_VIEW),

    // Users
    canViewUsers: () => can(PERMISSIONS.USERS_VIEW),
    canCreateUsers: () => can(PERMISSIONS.USERS_CREATE),
    canUpdateUsers: () => can(PERMISSIONS.USERS_UPDATE),
    canDeleteUsers: () => can(PERMISSIONS.USERS_DELETE),
    canManageRoles: () => can(PERMISSIONS.ROLES_MANAGE),

    // Settings
    canViewSettings: () => can(PERMISSIONS.SETTINGS_VIEW),
    canUpdateSettings: () => can(PERMISSIONS.SETTINGS_UPDATE),
    canManageTenantSettings: () => can(PERMISSIONS.TENANT_SETTINGS),

    // CRM - Leads
    canViewLeads: () => can(PERMISSIONS.LEADS_VIEW),
    canCreateLeads: () => can(PERMISSIONS.LEADS_CREATE),
    canUpdateLeads: () => can(PERMISSIONS.LEADS_UPDATE),
    canDeleteLeads: () => can(PERMISSIONS.LEADS_DELETE),
    canConvertLeads: () => can(PERMISSIONS.LEADS_CONVERT),

    // CRM - Clients
    canViewClients: () => can(PERMISSIONS.CLIENTS_VIEW),
    canCreateClients: () => can(PERMISSIONS.CLIENTS_CREATE),
    canUpdateClients: () => can(PERMISSIONS.CLIENTS_UPDATE),
    canDeleteClients: () => can(PERMISSIONS.CLIENTS_DELETE),

    // CRM - Contacts
    canViewContacts: () => can(PERMISSIONS.CONTACTS_VIEW),
    canCreateContacts: () => can(PERMISSIONS.CONTACTS_CREATE),
    canUpdateContacts: () => can(PERMISSIONS.CONTACTS_UPDATE),
    canDeleteContacts: () => can(PERMISSIONS.CONTACTS_DELETE),

    // Sales - Quotations
    canViewQuotations: () => can(PERMISSIONS.QUOTATIONS_VIEW),
    canCreateQuotations: () => can(PERMISSIONS.QUOTATIONS_CREATE),
    canUpdateQuotations: () => can(PERMISSIONS.QUOTATIONS_UPDATE),
    canDeleteQuotations: () => can(PERMISSIONS.QUOTATIONS_DELETE),
    canApproveQuotations: () => can(PERMISSIONS.QUOTATIONS_APPROVE),
    canSendQuotations: () => can(PERMISSIONS.QUOTATIONS_SEND),
    canConfirmQuotations: () => can(PERMISSIONS.QUOTATIONS_CONFIRM),

    // Sales - Orders
    canViewSalesOrders: () => can(PERMISSIONS.SALES_ORDERS_VIEW),
    canUpdateSalesOrders: () => can(PERMISSIONS.SALES_ORDERS_UPDATE),
    canDeleteSalesOrders: () => can(PERMISSIONS.SALES_ORDERS_DELETE),
    canInvoiceSalesOrders: () => can(PERMISSIONS.SALES_ORDERS_INVOICE),
    canCancelSalesOrders: () => can(PERMISSIONS.SALES_ORDERS_CANCEL),

    // Jobs
    canViewJobs: () => can(PERMISSIONS.JOBS_VIEW),
    canCreateJobs: () => can(PERMISSIONS.JOBS_CREATE),
    canUpdateJobs: () => can(PERMISSIONS.JOBS_UPDATE),
    canDeleteJobs: () => can(PERMISSIONS.JOBS_DELETE),
    canAssignJobs: () => can(PERMISSIONS.JOBS_ASSIGN),
    canUpdateJobStatus: () => can(PERMISSIONS.JOBS_STATUS),

    // Production
    canViewProduction: () => can(PERMISSIONS.PRODUCTION_VIEW),
    canUpdateProduction: () => can(PERMISSIONS.PRODUCTION_UPDATE),
    canManageProduction: () => can(PERMISSIONS.PRODUCTION_MANAGE),

    // Finance - Invoices
    canViewInvoices: () => can(PERMISSIONS.INVOICES_VIEW),
    canCreateInvoices: () => can(PERMISSIONS.INVOICES_CREATE),
    canUpdateInvoices: () => can(PERMISSIONS.INVOICES_UPDATE),
    canDeleteInvoices: () => can(PERMISSIONS.INVOICES_DELETE),
    canSendInvoices: () => can(PERMISSIONS.INVOICES_SEND),

    // Finance - Payments
    canViewPayments: () => can(PERMISSIONS.PAYMENTS_VIEW),
    canCreatePayments: () => can(PERMISSIONS.PAYMENTS_CREATE),
    canUpdatePayments: () => can(PERMISSIONS.PAYMENTS_UPDATE),

    // Finance - Expenses
    canViewExpenses: () => can(PERMISSIONS.EXPENSES_VIEW),
    canCreateExpenses: () => can(PERMISSIONS.EXPENSES_CREATE),
    canUpdateExpenses: () => can(PERMISSIONS.EXPENSES_UPDATE),
    canApproveExpenses: () => can(PERMISSIONS.EXPENSES_APPROVE),

    // Purchases
    canViewPurchases: () => can(PERMISSIONS.PURCHASES_VIEW),
    canCreatePurchases: () => can(PERMISSIONS.PURCHASES_CREATE),
    canUpdatePurchases: () => can(PERMISSIONS.PURCHASES_UPDATE),
    canApprovePurchases: () => can(PERMISSIONS.PURCHASES_APPROVE),

    // Vendors
    canViewVendors: () => can(PERMISSIONS.VENDORS_VIEW),
    canCreateVendors: () => can(PERMISSIONS.VENDORS_CREATE),
    canUpdateVendors: () => can(PERMISSIONS.VENDORS_UPDATE),
    canDeleteVendors: () => can(PERMISSIONS.VENDORS_DELETE),

    // Products
    canViewProducts: () => can(PERMISSIONS.PRODUCTS_VIEW),
    canCreateProducts: () => can(PERMISSIONS.PRODUCTS_CREATE),
    canUpdateProducts: () => can(PERMISSIONS.PRODUCTS_UPDATE),
    canDeleteProducts: () => can(PERMISSIONS.PRODUCTS_DELETE),

    // Reports
    canViewReports: () => can(PERMISSIONS.REPORTS_VIEW),
    canExportReports: () => can(PERMISSIONS.REPORTS_EXPORT),
    canCreateReports: () => can(PERMISSIONS.REPORTS_CREATE),

    // Attendance
    canViewAttendance: () => can(PERMISSIONS.ATTENDANCE_VIEW),
    canManageAttendance: () => can(PERMISSIONS.ATTENDANCE_MANAGE),
    canViewAttendanceReports: () => can(PERMISSIONS.ATTENDANCE_REPORTS),

    // Files
    canViewFiles: () => can(PERMISSIONS.FILES_VIEW),
    canUploadFiles: () => can(PERMISSIONS.FILES_UPLOAD),
    canDeleteFiles: () => can(PERMISSIONS.FILES_DELETE),

    // Automation
    canViewAutomation: () => can(PERMISSIONS.AUTOMATION_VIEW),
    canCreateAutomation: () => can(PERMISSIONS.AUTOMATION_CREATE),
    canUpdateAutomation: () => can(PERMISSIONS.AUTOMATION_UPDATE),
    canDeleteAutomation: () => can(PERMISSIONS.AUTOMATION_DELETE),
  };

  return {
    user,
    isSuperUser,
    isAdmin,
    isManager,
    can,
    canAny,
    canAll,
    ...permissions,
  };
}

/**
 * Component wrapper for permission-based rendering
 */
export function PermissionGate({
  children,
  permission,
  permissions,
  requireAll = false,
  fallback = null,
}: {
  children: React.ReactNode;
  permission?: string;
  permissions?: string[];
  requireAll?: boolean;
  fallback?: React.ReactNode;
}) {
  const { can, canAny, canAll } = usePermissions();

  let hasAccess = false;

  if (permission) {
    hasAccess = can(permission);
  } else if (permissions) {
    hasAccess = requireAll ? canAll(permissions) : canAny(permissions);
  }

  return hasAccess ? <>{children}</> : <>{fallback}</>;
}

export { PERMISSIONS };
